import React from "react";
import { useTranslation } from "../../localization/useTranslation";
import { useUsersDataMapping } from "./Users.hooks";
import { useUsersStyles } from "./Users.styles";
import UserIcon from "../users.svg?react";
import { UserColumn, UserRowTableModel, UserViewModel } from "../User.types";
import {
    DotLegalButton,
    DotLegalEmptyState,
    DotLegalPageHeader,
    DotLegalTable,
    DotLegalTableState,
    ITableHeader,
    useIsOnMediumScreen,
    useIsOnSmallScreen,
} from "@dotlegal/dotlegal-ui-components";
import { Box, TableCell } from "@mui/material";
import TableRow from "@common/components/tableRow/TableRow";
import { primaryArrowRight } from "../../common/icons";
import AddEditUserDialog from "../addEditUserDialog/AddEditUserDialog";
import TransferUserResponsibility from "../transferUserResponsibility/TransferUserResponsibility";
import { useUserContext } from "../../auth/userContextProvider/UserContextProvider";
import { usePlanContext } from "../../auth/planProvider/PlanProvider";
import FilterBar from "../../common/components/filterBar/FilterBar.tsx";
import ListPageToolbar from "@common/components/listPageToolbar/ListPageToolbar";
import SavedViewsMenu from "@common/components/savedViews/SavedViewsMenu";
import ExcelExportButton from "@common/components/excelExportButton/ExcelExportButton";
import TableDisplayFields from "@common/components/tableDisplayFields/TableDisplayFields";
import { FilterPage } from "@common/pageView/PageView.types";
import { UserFieldGroupingOrder } from "@common/components/filterPopover/FilterPopover.types";
import UserRoleNames from "../userRoleNames/UserRoleNames.tsx";
import TablePagePaper from "@common/components/tablePagePaper/TablePagePaper";

function Users() {
    const styles = useUsersStyles();
    const isSmallScreen = useIsOnSmallScreen();
    const isOnMediumScreen = useIsOnMediumScreen();
    const { permissions } = useUserContext();
    const { addonsFeatures } = usePlanContext();
    const { translateString } = useTranslation();
    const {
        isLoading,
        showAddUserDialog,
        setShowAddUserDialog,
        users,
        handleUserAddSuccess,
        goToUser,
        hasUsers,
        getBreadCrumbs,
        getFilterDataOptions,
        onFilterChange,
        clearFilterData,
        searchString,
        setSearchString,
        tableFields,
        visibleColumns,
        renderedColumns,
        tableHeaders,
        onColumnsSave,
        savedView,
        currentViewId,
        tableSessionStateKey,
        isSavedView,
        hasFilterChanged,
        hasDisplayChanged,
        filterData,
        onViewSaved,
        onViewDeleted,
        onChangeView,
        sortOrderBy,
        sortOrder,
        onSortChange,
        onDownloadExcelReportClick,
        pinnedFilterIds,
        setPinnedFilterIds,
    } = useUsersDataMapping();

    const headers: Array<ITableHeader<UserRowTableModel>> = tableHeaders.map((header) => ({ ...header, align: "left", showOnMobile: true }));

    const formatUserGroupEntities = (row: UserViewModel) => {
        if (row.gdpo) return translateString("all");

        const numberOfGroupEntities = row.userGroupEntities.length;
        if (numberOfGroupEntities === 0) return "";

        const maxNumberOfGroupEntitiesToShow = isSmallScreen || isOnMediumScreen ? 1 : 5;
        let extra;
        if (numberOfGroupEntities > maxNumberOfGroupEntitiesToShow) {
            extra = ` +${numberOfGroupEntities - maxNumberOfGroupEntitiesToShow}`;
        }
        const names = row.userGroupEntities
            .slice(0, maxNumberOfGroupEntitiesToShow)
            .map((g) => g.name)
            .join(", ");
        return (
            <Box component={"span"} sx={{ maxWidth: 450 }}>
                {names}
                <Box component={"b"}>{extra}</Box>
            </Box>
        );
    };

    const renderCell = (column: UserColumn, row: UserRowTableModel) => {
        switch (column) {
            case UserColumn.Name:
                return (
                    <TableCell key={column} sx={styles.bold}>
                        {row.fullName}
                    </TableCell>
                );
            case UserColumn.EmailAddress:
                return <TableCell key={column}>{row.emailAddress}</TableCell>;
            case UserColumn.BusinessAreas:
                return <TableCell key={column}>{row.businessAreas.map((x) => x.name).join(", ")}</TableCell>;
            case UserColumn.Roles:
                return (
                    <TableCell key={column}>
                        <UserRoleNames roles={row.userRoles} />
                    </TableCell>
                );
            case UserColumn.GroupEntities:
                return (
                    <TableCell key={column}>
                        <Box sx={styles.iconContainer}>{formatUserGroupEntities(row)}</Box>
                    </TableCell>
                );
            case UserColumn.LastLogin:
                return <TableCell key={column}>{row.lastLoginString}</TableCell>;
            case UserColumn.Created:
                return <TableCell key={column}>{row.createdString}</TableCell>;
            case UserColumn.Status:
                return <TableCell key={column}>{row.isActiveString}</TableCell>;
        }
    };

    return (
        <Box sx={styles.container}>
            <DotLegalPageHeader
                breadCrumbs={getBreadCrumbs()}
                rightComponent={
                    permissions.canManageUsers ? (
                        <Box sx={{ display: "flex", gap: 1 }}>
                            <TransferUserResponsibility buttonType="secondary" />
                            <DotLegalButton buttonType="primary" size="medium" onClick={() => setShowAddUserDialog(true)}>
                                {translateString("createUser")}
                            </DotLegalButton>
                        </Box>
                    ) : undefined
                }
            />

            <TablePagePaper>
                {hasUsers ? (
                    <React.Fragment>
                        <ListPageToolbar
                            savedViewsMenu={
                                <SavedViewsMenu
                                    currentViewId={currentViewId}
                                    parentIsLoading={isLoading}
                                    currentFilterData={filterData}
                                    currentDisplayData={visibleColumns}
                                    onViewChange={onChangeView}
                                    onViewSaved={onViewSaved}
                                    onViewDeleted={onViewDeleted}
                                    filterPage={FilterPage.Users}
                                    hasFilterOrDisplayDataChanged={hasFilterChanged || hasDisplayChanged}
                                />
                            }
                            leftContent={
                                <FilterBar
                                    isLoading={isLoading}
                                    filterData={getFilterDataOptions()}
                                    fetchFilterData
                                    onMenuClose={onFilterChange}
                                    onRemove={onFilterChange}
                                    pinnedFilterIds={pinnedFilterIds}
                                    onPinnedFilterIdsChange={setPinnedFilterIds}
                                    onClearFilter={hasFilterChanged ? () => clearFilterData() : undefined}
                                    clearFilterText={hasFilterChanged ? (isSavedView ? "savedView" : "defaultView") : undefined}
                                    searchValue={searchString}
                                    onSearchChange={setSearchString}
                                    searchLabel={translateString("search")}
                                />
                            }
                            rightContent={
                                <>
                                    {savedView && (
                                        <TableDisplayFields
                                            selectedFields={visibleColumns}
                                            onSave={onColumnsSave}
                                            options={tableFields}
                                            hasDisplayChanged={hasDisplayChanged}
                                            isCustomView={isSavedView}
                                            savedViewFields={savedView.displayData}
                                            groupingOrder={UserFieldGroupingOrder}
                                        />
                                    )}
                                    {savedView && (
                                        <ExcelExportButton
                                            onClick={onDownloadExcelReportClick}
                                            isDisabled={isLoading}
                                            isPlanLocked={!addonsFeatures.customLists}
                                            planLockedText={translateString("upgradeToCustomList")}
                                        />
                                    )}
                                </>
                            }
                        />

                        <DotLegalTable
                            sessionStateKey={tableSessionStateKey}
                            internalScroll
                            hasOverflowMenu
                            headers={headers}
                            defaultOrderBy={sortOrderBy}
                            defaultOrder={sortOrder}
                            // The table remembers its own sort within the session; the view still needs it so the sort
                            // is saved with the view and the export orders rows like the table. DotLegalTable emits
                            // onChange for page changes too, carrying the unchanged sort, so persist only a genuine sort
                            // change instead of rewriting the view on every page click.
                            onChange={(state: DotLegalTableState<UserRowTableModel>) => {
                                if (!state.property) return;
                                const orderBy = String(state.property);
                                if (orderBy !== sortOrderBy || state.order !== sortOrder) onSortChange({ orderBy, order: state.order });
                            }}
                            isLoading={isLoading}
                            searchString={searchString}
                            rowsPerPage={30}
                            emptyText={translateString("noUsersMatchesQuery")}
                            data={users}
                            renderRow={(row, i) => (
                                <TableRow
                                    inactive={!row.isActive}
                                    inactiveTooltip={translateString("deactivated")}
                                    onClick={() => goToUser(row.id)}
                                    key={i}
                                    sx={styles.tableRow}
                                >
                                    {renderedColumns.map((column) => renderCell(column, row))}
                                    <TableCell>
                                        <div className={`${styles.arrowContainer} ${styles.iconContainer}`}>{primaryArrowRight}</div>
                                    </TableCell>
                                </TableRow>
                            )}
                            paginationLabelOf={translateString("labelOf")}
                        />
                    </React.Fragment>
                ) : (
                    <DotLegalEmptyState icon={<UserIcon />} text={translateString("noUsers")} />
                )}
            </TablePagePaper>

            {showAddUserDialog && <AddEditUserDialog isEdit={false} onCloseDialog={() => setShowAddUserDialog(false)} onSaveCorrect={handleUserAddSuccess} />}
        </Box>
    );
}

export default Users;
